import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { contactInfo, images } from "../../data/content";
import { ArrowUpRight, Mail, AtSign, Download } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypesAr = ["مشاريع تطوير وإسكان", "مشاريع تجارية وضيافة", "مشاريع حيوية وخدمية", "أخرى"];
const projectTypesEn = ["Development & Housing", "Commercial & Hospitality", "Vital & Service", "Other"];
const projectSizesAr = ["أقل من 500 وحدة", "500 - 2,000 وحدة", "2,000 - 10,000 وحدة", "أكثر من 10,000 وحدة"];
const projectSizesEn = ["< 500 units", "500 - 2,000 units", "2,000 - 10,000 units", "> 10,000 units"];

const Contact = () => {
  const { isAr, lang, t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    project_size: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("الرجاء تعبئة الحقول الأساسية", "Please fill the required fields"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t("تم استلام طلبكم بنجاح", "Your request was received successfully"));
      setForm({ name: "", email: "", company: "", project_type: "", project_size: "", message: "" });
    } catch (err) {
      toast.error(t("حدث خطأ، حاول مرة أخرى", "Something went wrong, please try again"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#0F2F29] text-bg-ivory overflow-hidden"
      data-testid="contact-section"
    >
      <div className="relative z-10 max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14 py-[100px] md:py-[140px]">
        <div className="grid grid-cols-12 gap-x-12 gap-y-16">
          {/* Left: Headline + info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease }}
            className="col-span-12 md:col-span-5"
          >
            <div className="label-meta text-champagne/80 mb-5">[ SEC — 14 ]</div>
            <h3
              className={`${isAr ? "arabic-title" : "serif-title"} text-bg-ivory mb-8`}
              style={{ fontSize: isAr ? "clamp(38px, 4.4vw, 60px)" : "clamp(44px, 5vw, 84px)" }}
            >
              {isAr ? "تواصل معنا" : (<>Let's Build<br /><em className="font-light">Together</em></>)}
            </h3>
            {isAr ? (
              <p className="arabic-title text-bg-ivory/80" dir="rtl" style={{ fontSize: 16, lineHeight: 1.95, fontWeight: 400 }}>
                لندعم مشاريعك بكفاءة وجودة واعتمادية. سنرد عليك خلال ٢٤ ساعة.
              </p>
            ) : (
              <p className="text-bg-ivory/80 mb-2" style={{ fontSize: 15, lineHeight: 1.8 }}>
                Get in touch to discuss your project. We respond within 24 hours.
              </p>
            )}

            <div className="space-y-5">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 py-4 mt-10 border-t border-b border-champagne/22 group cursor-hover"
                data-testid="email-link"
              >
                <Mail size={18} className="text-champagne" />
                <span className="text-bg-ivory" style={{ fontSize: 15 }}>{contactInfo.email}</span>
                <ArrowUpRight size={16} className="ml-auto text-champagne/70 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform flip-x" />
              </a>
              <div className="flex items-center gap-4 py-4 border-b border-champagne/22">
                <AtSign size={18} className="text-champagne" />
                <span className="text-bg-ivory" style={{ fontSize: 15 }}>{contactInfo.social}</span>
              </div>
            </div>

            {/* PDF Download */}
            <a
              href="https://customer-assets.emergentagent.com/job_8041b13e-5dc7-4e75-b3bf-b00ec9e6bcfd/artifacts/gk4kr3op_Bigbrand%20Profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 bb-btn bb-btn-ghost-dark cursor-hover"
              data-testid="pdf-download"
            >
              <Download size={16} />
              {t("تحميل ملف الشركة", "Download Company Profile")}
            </a>

            {/* QR + Meta */}
            <div className="mt-14 grid grid-cols-2 gap-6 items-end">
              <div>
                <img
                  src={images.qr}
                  alt={t("رمز QR لملف الشركة", "Company profile QR code")}
                  className="w-[110px] h-[110px] object-contain"
                  data-testid="contact-qr"
                />
                <div className="label-meta text-champagne/70 mt-3">SCAN QR</div>
              </div>
              <div className="text-end">
                {contactInfo.meta.map((m, i) => (
                  <div key={i} className="label-meta text-bg-ivory/70 mb-1" style={{ fontSize: 10 }}>
                    {m}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease, delay: 0.15 }}
            className="col-span-12 md:col-span-7"
          >
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-meta text-champagne/80 block mb-2">
                    {t("الاسم *", "Name *")}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="bb-input-dark"
                    placeholder={t("اسمك الكامل", "Your full name")}
                    required
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="label-meta text-champagne/80 block mb-2">
                    {t("البريد الإلكتروني *", "Email *")}
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="bb-input-dark"
                    placeholder="email@company.com"
                    required
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-meta text-champagne/80 block mb-2">
                    {t("الشركة", "Company")}
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    className="bb-input-dark"
                    placeholder={t("اسم الشركة", "Company name")}
                    data-testid="form-company"
                  />
                </div>
                <div>
                  <label className="label-meta text-champagne/80 block mb-2">
                    {t("نوع المشروع", "Project Type")}
                  </label>
                  <select
                    value={form.project_type}
                    onChange={update("project_type")}
                    className="bb-input-dark"
                    style={{ background: "#0F2F29" }}
                    data-testid="form-project-type"
                  >
                    <option value="">{t("اختر النوع", "Select type")}</option>
                    {(isAr ? projectTypesAr : projectTypesEn).map((p, i) => (
                      <option key={i} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label-meta text-champagne/80 block mb-2">
                  {t("حجم المشروع", "Project Size")}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  {(isAr ? projectSizesAr : projectSizesEn).map((s, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setForm((f) => ({ ...f, project_size: s }))}
                      className={`py-3 px-3 text-xs border transition-all duration-500 cursor-hover ${
                        form.project_size === s
                          ? "bg-champagne text-brand-green border-champagne"
                          : "border-champagne/30 text-bg-ivory/85 hover:border-champagne"
                      }`}
                      data-testid={`size-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-meta text-champagne/80 block mb-2">
                  {t("الرسالة *", "Message *")}
                </label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className="bb-input-dark"
                  style={{ resize: "none", paddingTop: 14 }}
                  placeholder={t("صف مشروعك ومتطلباتك...", "Describe your project and requirements...")}
                  required
                  data-testid="form-message"
                />
              </div>

              <div className="pt-6 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
                <div className="label-meta text-bg-ivory/55">
                  {t("نرد خلال 24 ساعة", "We respond within 24 hours")}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bb-btn cursor-hover"
                  style={{ background: "#E8D3C3", color: "#173F35", borderColor: "#E8D3C3" }}
                  data-testid="form-submit"
                >
                  {loading
                    ? t("جاري الإرسال...", "Sending...")
                    : t("إرسال الطلب", "Send Request")}
                  <ArrowUpRight size={16} className="flip-x" />
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
